// Centralized role configuration for maintainability
// This file defines all role-specific behaviors, permissions, and access patterns

import { getDisplayValue } from '@/utils/studentUtils'
import { canManageMeetings, getStudentCaseManagerId } from '@/utils/meetingPermissions'
import { isAllScopePlan, isOtherPlan } from '@/utils/studentPlan.js'
import { areEvaluationsEnabled } from '@/utils/environment'
import { EVAL_PROVIDER_VIEW, isPlanCaseloadStudent } from '@/utils/studentEvaluation'

// ─── ROLE DEFINITIONS ──────────────────────────────────────────────────────────
export const ROLES = {
  ADMIN: 'admin',
  // District tenancy (see Developer/DISTRICT_TENANCY_DESIGN.md). Registered in
  // Phase 1 and not yet surfaced in the role picker (not in BASE_ROLES_FOR_DROPDOWN).
  // firestore.rules DO enforce district roles + school scoping (inScope helpers).
  DISTRICT_ADMIN: 'district_admin',
  DISTRICT_COORDINATOR: 'district_coordinator',
  SCHOOL_ADMIN: 'school_admin',
  ADMIN_504: 'admin_504',
  SPED_CHAIR: 'sped_chair',
  SPED_CHAIR_ALL: 'sped_chair_all',
  STAFF_VIEW: 'staff_view',
  STAFF_EDIT: 'staff_edit',
  STAFF_EDIT_DOCS: 'staff_edit_docs',
  CASE_MANAGER: 'case_manager',
  TEACHER: 'teacher',
  TEACHER_FULL: 'teacher_full',
  CASE_MANAGER_FULL: 'case_manager_full',
  SERVICE_PROVIDER: 'service_provider',
  SPEECH_CASE_MANAGER: 'speech_case_manager',
  SCHOOL_PSYCH: 'school_psych',
  PARAEDUCATOR: 'paraeducator'
}

// ─── ROLE HIERARCHY ───────────────────────────────────────────────────────────
export const ROLE_HIERARCHY = {
  [ROLES.ADMIN]: 100,
  // District tenancy: hierarchy is used for UI management ("who can manage whom")
  // only — it is NOT a security boundary (firestore.rules use explicit role
  // checks). district_admin/coordinator sit above school_admin so they can
  // manage school-level users in a later phase.
  [ROLES.DISTRICT_ADMIN]: 95,
  [ROLES.DISTRICT_COORDINATOR]: 93,
  [ROLES.SCHOOL_ADMIN]: 90,
  [ROLES.SPED_CHAIR_ALL]: 82,
  [ROLES.SPED_CHAIR]: 80,
  [ROLES.ADMIN_504]: 70,
  [ROLES.STAFF_EDIT]: 65,
  [ROLES.STAFF_EDIT_DOCS]: 55,
  [ROLES.CASE_MANAGER_FULL]: 62,
  [ROLES.CASE_MANAGER]: 60,
  [ROLES.SPEECH_CASE_MANAGER]: 60,
  [ROLES.SERVICE_PROVIDER]: 50,
  [ROLES.SCHOOL_PSYCH]: 47,
  [ROLES.STAFF_VIEW]: 45,
  [ROLES.TEACHER_FULL]: 42,
  [ROLES.TEACHER]: 40,
  [ROLES.PARAEDUCATOR]: 30
}

// ─── PERMISSIONS MATRIX ───────────────────────────────────────────────────────
export const PERMISSIONS = {
  // Student Access Permissions
  CAN_VIEW_ALL_STUDENTS: 'canViewAllStudents',
  CAN_EDIT_ALL_STUDENTS: 'canEditAllStudents',
  CAN_EDIT_OWN_STUDENTS: 'canEditOwnStudents',
  CAN_SEND_FEEDBACK: 'canSendFeedback',

  // System Management Permissions
  CAN_MANAGE_USERS: 'canManageUsers',
  CAN_MANAGE_AIDES: 'canManageAides',
  CAN_ACCESS_TESTING: 'canAccessTesting',

  // UI Permissions
  CAN_ACCESS_FILTERS: 'canAccessFilters',
  SHOW_PROVIDER_VIEW: 'showProviderView',
  CAN_USE_CLASS_VIEW: 'canUseClassView'
}

export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_EDIT_ALL_STUDENTS,
    PERMISSIONS.CAN_SEND_FEEDBACK,
    PERMISSIONS.CAN_MANAGE_USERS,
    PERMISSIONS.CAN_MANAGE_AIDES,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.CAN_ACCESS_FILTERS,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  // District Admin: all-school data access (gated by inScope = all schools for
  // this role) plus full district operations. Mirrors admin powers.
  [ROLES.DISTRICT_ADMIN]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_EDIT_ALL_STUDENTS,
    PERMISSIONS.CAN_SEND_FEEDBACK,
    PERMISSIONS.CAN_MANAGE_USERS,
    PERMISSIONS.CAN_MANAGE_AIDES,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.CAN_ACCESS_FILTERS,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  // District Coordinator: district operations (user/school management) with NO
  // student PII access — deliberately omits all student view/edit permissions.
  [ROLES.DISTRICT_COORDINATOR]: [PERMISSIONS.CAN_MANAGE_USERS],

  [ROLES.SPED_CHAIR]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_EDIT_ALL_STUDENTS,
    PERMISSIONS.CAN_SEND_FEEDBACK,
    PERMISSIONS.CAN_MANAGE_USERS,
    PERMISSIONS.CAN_MANAGE_AIDES,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.CAN_ACCESS_FILTERS,
    PERMISSIONS.SHOW_PROVIDER_VIEW,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  [ROLES.SPED_CHAIR_ALL]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_EDIT_ALL_STUDENTS,
    PERMISSIONS.CAN_SEND_FEEDBACK,
    PERMISSIONS.CAN_MANAGE_USERS,
    PERMISSIONS.CAN_MANAGE_AIDES,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.CAN_ACCESS_FILTERS,
    PERMISSIONS.SHOW_PROVIDER_VIEW,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  [ROLES.SCHOOL_ADMIN]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_EDIT_ALL_STUDENTS,
    PERMISSIONS.CAN_SEND_FEEDBACK,
    PERMISSIONS.CAN_MANAGE_USERS,
    PERMISSIONS.CAN_MANAGE_AIDES,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.CAN_ACCESS_FILTERS,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  [ROLES.STAFF_VIEW]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.CAN_ACCESS_FILTERS,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  [ROLES.STAFF_EDIT]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_EDIT_ALL_STUDENTS,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.CAN_ACCESS_FILTERS,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  [ROLES.STAFF_EDIT_DOCS]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_EDIT_ALL_STUDENTS,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.CAN_ACCESS_FILTERS,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  // 504 Coordinator: full peer of school_admin for student edit (also carries a
  // caseload). Matches canEditStudent() and the rules super-admin tier.
  [ROLES.ADMIN_504]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_EDIT_ALL_STUDENTS,
    PERMISSIONS.CAN_SEND_FEEDBACK,
    PERMISSIONS.CAN_MANAGE_USERS,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.CAN_ACCESS_FILTERS,
    PERMISSIONS.SHOW_PROVIDER_VIEW,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  [ROLES.CASE_MANAGER]: [
    PERMISSIONS.CAN_EDIT_OWN_STUDENTS,
    PERMISSIONS.CAN_SEND_FEEDBACK,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.SHOW_PROVIDER_VIEW,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  [ROLES.TEACHER]: [PERMISSIONS.CAN_ACCESS_TESTING, PERMISSIONS.CAN_USE_CLASS_VIEW],

  [ROLES.TEACHER_FULL]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.SHOW_PROVIDER_VIEW,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  [ROLES.CASE_MANAGER_FULL]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_EDIT_OWN_STUDENTS,
    PERMISSIONS.CAN_SEND_FEEDBACK,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.SHOW_PROVIDER_VIEW,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  // Service providers are view-only for students (no caseload, no edit) — see
  // canEditStudent() and the role capability contract. No scope pills: All and
  // SP were the same set (staffIds), so they just see their accessible students.
  [ROLES.SERVICE_PROVIDER]: [PERMISSIONS.CAN_ACCESS_TESTING, PERMISSIONS.CAN_USE_CLASS_VIEW],

  [ROLES.SPEECH_CASE_MANAGER]: [
    PERMISSIONS.CAN_EDIT_OWN_STUDENTS,
    PERMISSIONS.CAN_SEND_FEEDBACK,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.SHOW_PROVIDER_VIEW,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  [ROLES.SCHOOL_PSYCH]: [
    PERMISSIONS.CAN_VIEW_ALL_STUDENTS,
    PERMISSIONS.CAN_SEND_FEEDBACK,
    PERMISSIONS.CAN_ACCESS_TESTING,
    PERMISSIONS.CAN_ACCESS_FILTERS,
    PERMISSIONS.CAN_USE_CLASS_VIEW
  ],

  [ROLES.PARAEDUCATOR]: [PERMISSIONS.CAN_ACCESS_TESTING, PERMISSIONS.CAN_USE_CLASS_VIEW]
}

// ─── PROVIDER VIEW OPTIONS ────────────────────────────────────────────────────
export const PROVIDER_VIEW_OPTIONS = {
  [ROLES.ADMIN]: [
    { value: 'all', label: 'All' },
    { value: EVAL_PROVIDER_VIEW, label: 'Initial' }
  ],
  [ROLES.DISTRICT_ADMIN]: [
    { value: 'all', label: 'All' },
    { value: EVAL_PROVIDER_VIEW, label: 'Initial' }
  ],
  [ROLES.SCHOOL_ADMIN]: [
    { value: 'all', label: 'All' },
    { value: EVAL_PROVIDER_VIEW, label: 'Initial' }
  ],
  [ROLES.SCHOOL_PSYCH]: [
    { value: 'all', label: 'All' },
    { value: EVAL_PROVIDER_VIEW, label: 'Initial' }
  ],

  [ROLES.SPED_CHAIR]: [
    { value: 'all_iep', label: 'IEP' },
    { value: 'case_manager', label: 'CM' },
    { value: 'service_provider', label: 'SP' },
    { value: EVAL_PROVIDER_VIEW, label: 'Initial' }
  ],

  // All first (consistent with other "full" roles). No 504 pill — use plan filter on All.
  [ROLES.SPED_CHAIR_ALL]: [
    { value: 'all', label: 'All' },
    { value: 'all_iep', label: 'IEP' },
    { value: 'case_manager', label: 'CM' },
    { value: 'service_provider', label: 'SP' },
    { value: EVAL_PROVIDER_VIEW, label: 'Initial' }
  ],

  // 504 Coordinator
  [ROLES.ADMIN_504]: [
    { value: 'case_manager', label: 'CM' },
    { value: 'iep_504_all', label: 'All' },
    { value: EVAL_PROVIDER_VIEW, label: 'Initial' }
  ],

  // Teacher (All): All on the left; default selection is My Students (own classes).
  // List view honors this; class view is always grouped by the teacher's own periods.
  [ROLES.TEACHER_FULL]: [
    { value: 'all', label: 'All' },
    { value: 'teacher', label: 'My Students' }
  ],

  // Case Manager: CM / SP only — no "All" pill. The combined set was exactly
  // CM ∪ SP, and the "All" label collided with the school-wide "All" on the
  // *_full roles. Default lands on the caseload (matches the todo banner,
  // meetings, and feedback, which are all caseload-driven).
  [ROLES.CASE_MANAGER]: [
    { value: 'case_manager', label: 'CM' },
    { value: 'service_provider', label: 'SP' },
    { value: EVAL_PROVIDER_VIEW, label: 'Initial' }
  ],

  [ROLES.CASE_MANAGER_FULL]: [
    { value: 'all', label: 'All' },
    { value: 'case_manager', label: 'CM' },
    { value: 'service_provider', label: 'SP' },
    { value: EVAL_PROVIDER_VIEW, label: 'Initial' }
  ],

  // Speech Case Manager: same CM / SP structure as case_manager (SP = students
  // served via staffIds). Extended-access students surface under SP.
  [ROLES.SPEECH_CASE_MANAGER]: [
    { value: 'case_manager', label: 'CM' },
    { value: 'service_provider', label: 'SP' },
    { value: EVAL_PROVIDER_VIEW, label: 'Initial' }
  ]
}

// ─── DEFAULT PROVIDER VIEWS ───────────────────────────────────────────────────
export const DEFAULT_PROVIDER_VIEWS = {
  [ROLES.ADMIN]: 'all',
  [ROLES.DISTRICT_ADMIN]: 'all',
  [ROLES.SCHOOL_ADMIN]: 'all',
  [ROLES.SCHOOL_PSYCH]: 'all',
  [ROLES.SPED_CHAIR]: 'all_iep',
  [ROLES.SPED_CHAIR_ALL]: 'all',
  [ROLES.ADMIN_504]: 'case_manager',
  [ROLES.CASE_MANAGER]: 'case_manager',
  [ROLES.CASE_MANAGER_FULL]: 'all',
  [ROLES.SPEECH_CASE_MANAGER]: 'case_manager',
  // Daily teaching workflow: own classes first; All remains available as a scope pill.
  [ROLES.TEACHER_FULL]: 'teacher'
}

// ─── STUDENT ACCESS PATTERNS ──────────────────────────────────────────────────
export const STUDENT_ACCESS_PATTERNS = {
  // Case Manager Access: Students they case manage AND students they provide services to
  [ROLES.CASE_MANAGER]: {
    patterns: ['case_manager', 'teacher'],
    description: 'Students in their caseload and students they provide services to'
  },

  // Speech Case Manager Access: Same as case managers but with speech specialization
  [ROLES.SPEECH_CASE_MANAGER]: {
    patterns: ['case_manager', 'speech_service'],
    description: 'Students in their caseload and students they provide speech services to'
  },

  // Teacher Access: Students they teach
  [ROLES.TEACHER]: {
    patterns: ['teacher'],
    description: 'Students in their classes'
  },

  // Teacher Full Access: All students (with class view filtered to their classes)
  [ROLES.TEACHER_FULL]: {
    patterns: ['all'],
    description: 'All students (class view shows only their assigned classes)'
  },

  // Case Manager Full Access: All students (with CM functionality for own caseload)
  [ROLES.CASE_MANAGER_FULL]: {
    patterns: ['all'],
    description: 'All students (can edit and send feedback for own caseload)'
  },

  // Service Provider Access: Students they serve (app.providers → staffIds)
  [ROLES.SERVICE_PROVIDER]: {
    patterns: ['speech_service'],
    description: 'Students they are assigned to serve via staffIds/providers'
  },

  // Paraeducator Access: Students they assist
  [ROLES.PARAEDUCATOR]: {
    patterns: ['paraeducator'],
    description: 'Students they are assigned to assist'
  },

  // SPED Chair Access: IEP students (with filtering options)
  [ROLES.SPED_CHAIR]: {
    patterns: ['case_manager', 'teacher', 'all_iep'],
    description: 'IEP students, plus students they case manage or teach'
  },

  // SPED Chair All Access: IEP, 504, and Other
  [ROLES.SPED_CHAIR_ALL]: {
    patterns: ['case_manager', 'teacher', 'all_iep_504'],
    description: 'IEP, 504, and Other students, plus students they case manage or teach'
  },

  // 504 Coordinator Access: own caseload, plus every IEP, 504, and Other student
  [ROLES.ADMIN_504]: {
    patterns: ['case_manager', 'all_iep_504'],
    description: 'Own caseload, plus every IEP, 504, and Other student'
  },

  // Admin Access: All students
  [ROLES.ADMIN]: {
    patterns: ['all'],
    description: 'All students'
  },

  // District Admin Access: all students within scope (scope = all schools for
  // this role, enforced by inScope in firestore.rules).
  // NOTE: district_coordinator is intentionally absent — no student PII access.
  [ROLES.DISTRICT_ADMIN]: {
    patterns: ['all'],
    description: 'All students across the district'
  },

  // School Admin Access: All students
  [ROLES.SCHOOL_ADMIN]: {
    patterns: ['all'],
    description: 'All students'
  },

  // Staff View Access: All students (view-only)
  [ROLES.STAFF_VIEW]: {
    patterns: ['all'],
    description: 'All students (view-only)'
  },

  // Staff Edit Access: All students (can edit)
  [ROLES.STAFF_EDIT]: {
    patterns: ['all'],
    description: 'All students (can edit)'
  },

  // Staff Edit Docs Access: All students (can edit documents and flags only)
  [ROLES.STAFF_EDIT_DOCS]: {
    patterns: ['all'],
    description: 'All students (can edit documents and flags only)'
  },

  // School Psychologist Access: All students (view only)
  [ROLES.SCHOOL_PSYCH]: {
    patterns: ['all'],
    description: 'All students (view only, can create feedback forms)'
  }
}

// ─── ACCESS PATTERN IMPLEMENTATIONS ───────────────────────────────────────────
export const ACCESS_PATTERN_FUNCTIONS = {
  // Check if user is case manager of student
  case_manager: (userId, student, _studentData) => {
    // Plan CM only. Eval-only assignments live on the EV pill.
    // Extended access students should NOT appear in CM-only view.
    return isPlanCaseloadStudent(student, userId)
  },

  // Check if user teaches student
  teacher: (userId, student, studentData) => {
    // Extended/filtered access students should be accessible
    if (student._extendedAccess || student._isFiltered) return true

    const schedule = studentData.getSchedule(student)
    if (!schedule) return false

    return Object.values(schedule).some((periodData) => {
      if (typeof periodData === 'string') {
        return periodData === userId
      } else if (periodData && typeof periodData === 'object') {
        return periodData.teacherId === userId || periodData.coTeaching?.caseManagerId === userId
      }
      return false
    })
  },

  // Check if user provides services to student (via staffIds / providers).
  // Also honors extended-access / filtered rows loaded for the user.
  speech_service: (userId, student, studentData) => {
    if (student._extendedAccess || student._isFiltered) return true
    const staffIds = student.app?.staffIds || []
    return staffIds.includes(userId)
  },

  // Check if user is paraeducator assigned to student
  paraeducator: (userId, student, studentData, aideAssignment) => {
    if (!aideAssignment) return false

    // Get the aide data for this user
    const aideData = aideAssignment[userId]
    if (!aideData) return false

    // Check direct assignment first
    const directStudentIds = Array.isArray(aideData.directAssignment)
      ? aideData.directAssignment
      : aideData.directAssignment
        ? [aideData.directAssignment]
        : []
    if (directStudentIds.includes(student.id)) return true

    // Check class assignment
    const schedule = studentData.getSchedule(student)
    if (!schedule) return false

    const aidePeriods = aideData.classAssignment || {}
    return Object.entries(schedule).some(([period, data]) => {
      const teacherId = typeof data === 'string' ? data : data?.teacherId
      const aideTeacherIds = aidePeriods[period]
      if (!aideTeacherIds || !teacherId) return false
      const teacherIdArray = Array.isArray(aideTeacherIds) ? aideTeacherIds : [aideTeacherIds]
      return teacherIdArray.includes(teacherId)
    })
  },

  // Check if student has IEP
  all_iep: (userId, student, studentData) => {
    const plan = getDisplayValue(student, 'plan')
    return plan === 'IEP'
  },

  // School-wide All for SPED Chair (All) and the 504 coordinator: IEP, 504, and Other.
  all_iep_504: (userId, student, studentData) => {
    const plan = getDisplayValue(student, 'plan')
    return isAllScopePlan(plan)
  },

  // Check if student has 504
  all_504: (userId, student, studentData) => {
    const plan = getDisplayValue(student, 'plan')
    return plan === '504'
  },

  // All students (no filtering)
  all: () => true
}

// ─── FEEDBACK FORM ACCESS RULES ───────────────────────────────────────────────
// Feedback forms follow the MEETING model: only the student's case manager of
// record (any CM-assignable role) may create one — plus the 504 coordinator
// for 504 students. Predicates delegate to canManageMeetings so the two
// surfaces can never drift. Plain admins lose feedback creation, exactly like
// meeting scheduling.
//
// The ONE exception is school psychologists: they may create feedback forms
// for ANY student (reevaluation feedback is psych-driven). The student's case
// manager is expected to be a shared viewer on psych-created forms.
//
// UI-only gate — firestore.rules for feedbackInstances are deliberately
// broader and unchanged.
const caseloadOnlyFeedback = (role) => ({
  canSendFeedback: (userId, student) => canManageMeetings({ uid: userId, role }, student),
  description: 'Can send feedback for students in their caseload'
})

export const FEEDBACK_ACCESS_RULES = {
  [ROLES.CASE_MANAGER]: caseloadOnlyFeedback(ROLES.CASE_MANAGER),
  [ROLES.CASE_MANAGER_FULL]: caseloadOnlyFeedback(ROLES.CASE_MANAGER_FULL),
  [ROLES.SPEECH_CASE_MANAGER]: caseloadOnlyFeedback(ROLES.SPEECH_CASE_MANAGER),
  [ROLES.SPED_CHAIR]: caseloadOnlyFeedback(ROLES.SPED_CHAIR),
  [ROLES.SPED_CHAIR_ALL]: caseloadOnlyFeedback(ROLES.SPED_CHAIR_ALL),

  [ROLES.ADMIN_504]: {
    // Other plans (SST/RTI/MTSS) hold no calendar meetings, but teacher
    // feedback is exactly what an SST meeting runs on — so the coordinator
    // keeps feedback for the Other students they case-manage even though
    // canManageMeetings() denies the whole plan.
    canSendFeedback: (userId, student) =>
      canManageMeetings({ uid: userId, role: ROLES.ADMIN_504 }, student) ||
      (isOtherPlan(student) && getStudentCaseManagerId(student) === userId),
    description:
      'Can send feedback for students they case manage (including Other plans) or any 504 student'
  },

  [ROLES.SCHOOL_PSYCH]: {
    canSendFeedback: () => true,
    description: 'Can send feedback for all students (reevaluation exception)'
  }

  // admin / district_admin / school_admin deliberately omitted: feedback
  // creation belongs to case managers, mirroring meeting scheduling.
}

// ─── ROLE OPTIONS MAP ─────────────────────────────────────────────────────────
// Maps concrete roles to base roles + UI options for admin user management
export const ROLE_OPTIONS_MAP = {
  teacher: { base: 'teacher', options: {} },
  teacher_full: { base: 'teacher', options: { seeAllStudents: true } },
  case_manager: { base: 'case_manager', options: {} },
  case_manager_full: { base: 'case_manager', options: { seeAllStudents: true } },
  sped_chair: { base: 'sped_chair', options: {} },
  sped_chair_all: { base: 'sped_chair', options: { include504: true } },
  admin: { base: 'admin', options: {} },
  school_admin: { base: 'school_admin', options: {} },
  admin_504: { base: 'admin_504', options: {} },
  staff_view: { base: 'staff_view', options: {} },
  staff_edit_docs: { base: 'staff_edit', options: {} },
  staff_edit: { base: 'staff_edit', options: { fullEdit: true } },
  service_provider: { base: 'service_provider', options: {} },
  speech_case_manager: { base: 'speech_case_manager', options: {} },
  school_psych: { base: 'school_psych', options: {} },
  paraeducator: { base: 'paraeducator', options: {} }
}

// Available role options per base role (defines what checkboxes to show)
export const BASE_ROLE_OPTIONS = {
  teacher: [
    {
      key: 'seeAllStudents',
      label: 'All students',
      badge: 'All',
      tooltip: 'Access to all students',
      targetRole: 'teacher_full'
    }
  ],
  case_manager: [
    {
      key: 'seeAllStudents',
      label: 'All students',
      badge: 'All',
      tooltip: 'Access to all students',
      targetRole: 'case_manager_full'
    }
  ],
  sped_chair: [
    {
      key: 'include504',
      label: 'All (IEP + 504)',
      badge: 'All',
      tooltip: 'Access to both IEP and 504 students',
      targetRole: 'sped_chair_all'
    }
  ],
  staff_edit: [
    {
      key: 'fullEdit',
      label: 'Full editing',
      badge: 'Full',
      tooltip: 'Can edit all student data',
      targetRole: 'staff_edit'
    }
  ]
}

// When no option is selected, some base roles map to a different concrete default
export const BASE_ROLE_DEFAULTS = {
  staff_edit: 'staff_edit_docs'
}

// Labels for the "no option selected" radio per base role
export const BASE_DEFAULT_LABELS = {
  staff_edit: 'Docs only'
}

// Resolve a base role + selected option to a concrete role
export function resolveRole(baseRole, selectedOption) {
  const options = BASE_ROLE_OPTIONS[baseRole]
  if (!options || !selectedOption) return BASE_ROLE_DEFAULTS[baseRole] || baseRole

  const option = options.find((o) => o.key === selectedOption)
  return option ? option.targetRole : BASE_ROLE_DEFAULTS[baseRole] || baseRole
}

// Get the base role and active option from a concrete role
export function decomposeRole(concreteRole) {
  const mapping = ROLE_OPTIONS_MAP[concreteRole]
  if (!mapping) return { base: concreteRole, activeOption: null }

  const activeOptions = Object.entries(mapping.options).filter(([, v]) => v)
  const activeOption = activeOptions.length > 0 ? activeOptions[0][0] : null
  return { base: mapping.base, activeOption }
}

// Base roles for the role dropdown (excludes variant roles)
export const BASE_ROLES_FOR_DROPDOWN = [
  'admin',
  'school_admin',
  'staff_view',
  'staff_edit',
  'admin_504',
  'sped_chair',
  'case_manager',
  'teacher',
  'service_provider',
  'speech_case_manager',
  'school_psych',
  'paraeducator'
]

// ─── UTILITY FUNCTIONS ────────────────────────────────────────────────────────
export const RoleUtils = {
  // Check if user has a specific permission
  hasPermission: (userRole, permission) => {
    const permissions = ROLE_PERMISSIONS[userRole] || []
    return permissions.includes(permission)
  },

  // Get provider view options for a role
  getProviderViewOptions: (userRole) => {
    const options = PROVIDER_VIEW_OPTIONS[userRole] || []
    if (areEvaluationsEnabled()) return options
    const withoutEv = options.filter((option) => option.value !== EVAL_PROVIDER_VIEW)
    // Admin / psych only gained pills so they can reach EV. With evals off
    // they go back to an unpilled full list.
    if (
      [ROLES.ADMIN, ROLES.DISTRICT_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.SCHOOL_PSYCH].includes(
        userRole
      ) &&
      withoutEv.length === 1 &&
      withoutEv[0].value === 'all'
    ) {
      return []
    }
    return withoutEv
  },

  // Get default provider view for a role
  getDefaultProviderView: (userRole) => {
    return DEFAULT_PROVIDER_VIEWS[userRole] || 'all'
  },

  // Check if user can access student
  canAccessStudent: (userId, userRole, student, studentData, aideAssignment = null) => {
    const patterns = STUDENT_ACCESS_PATTERNS[userRole]?.patterns || []

    return patterns.some((pattern) => {
      const patternFunction = ACCESS_PATTERN_FUNCTIONS[pattern]
      return patternFunction ? patternFunction(userId, student, studentData, aideAssignment) : false
    })
  },

  // Check if user can send feedback for student
  canSendFeedback: (userId, userRole, student, studentData) => {
    const rule = FEEDBACK_ACCESS_RULES[userRole]
    return rule ? rule.canSendFeedback(userId, student, studentData) : false
  },

  // Get role hierarchy level
  getRoleLevel: (userRole) => {
    return ROLE_HIERARCHY[userRole] || 0
  },

  // Check if role has higher privileges than another
  hasHigherPrivileges: (role1, role2) => {
    return RoleUtils.getRoleLevel(role1) > RoleUtils.getRoleLevel(role2)
  }
}

export default RoleUtils
